import React, { useState, useEffect } from "react";

function EditProjectModal({ isOpen, onClose, initialData }) {
  const [formData, setFormData] = useState(() => {
    return {
      customerName: initialData.customerName || "",
      street: initialData.street || "",
      city: initialData.city || "",
      state: initialData.state || "",
      zip: initialData.zipCode || "",
      billingDate: initialData.billingDate || "",
      taxExempt: initialData.taxExempt || "",
      certifiedPayroll: initialData.certifiedPayroll || "",
      ppBonds: initialData.ppBonds || "",
      ccip: initialData.ccip || "",
    };
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [zipError, setZipError] = useState('');

  // Function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Validate zip code whenever it changes
  useEffect(() => {
    validateZipCode(formData.zip);
  }, [formData.zip]);

  // Function to validate zip code
  const validateZipCode = (zip) => {
    if (!zip) {
      setZipError('');
      return true;
    }
    
    const zipRegex = /^\d{1,5}$/;
    if (!zipRegex.test(zip)) {
      setZipError('Please enter only digits (0-9)');
      return false;
    }
    
    if (zip.length !== 5 && zip.length !== 0) {
      setZipError('Zip code must be exactly 5 digits');
      return false;
    }
    
    setZipError('');
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for zip code
    if (name === 'zip') {
      // Only allow digits and limit to 5 characters
      const sanitizedValue = value.replace(/\D/g, '').slice(0, 5);
      setFormData({ ...formData, [name]: sanitizedValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSave = async () => {
    try {
      // Validate zip code before submitting
      if (!validateZipCode(formData.zip)) {
        showNotification("Please fix the zip code error before saving", "error");
        return;
      }
      
      setIsSubmitting(true);

      // First, update the local storage as before
      const projectAddress = `${formData.street}, ${formData.city}, ${formData.state} ${formData.zip}`;
      const updatedData = {
        ...initialData,
        customerName: formData.customerName,
        projectAddress,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zip,
        billingDate: formData.billingDate,
        taxExempt: formData.taxExempt,
        certifiedPayroll: formData.certifiedPayroll,
        ppBonds: formData.ppBonds,
        ccip: formData.ccip,
      };

      localStorage.setItem("selectedProjectDetails", JSON.stringify(updatedData));

      // Log the initialData to see what's available
      console.log("initialData:", initialData);

      // Use the salesforceProjectId that was saved from ManageProject.jsx
      const projectId = initialData.salesforceProjectId;
      
      // Log the projectId we're using
      console.log("Using projectId:", projectId);
      
      // If we have a project ID, make the API call
      if (projectId) {
        // Prepare the request body according to the API requirements
        const requestBody = {
          salesforce_id: projectId,
          gmb_City__c: formData.city,
          gmb_Project_State__c: formData.state,
          Street__c: formData.street,
          Zip_Code__c: formData.zip,
          gmb_Earliest_Bid_Date__c: formData.billingDate,
          tax_exempt: formData.taxExempt,
          certified_payroll: formData.certifiedPayroll,
          p_and_p_bonds: formData.ppBonds,
          ccip: formData.ccip
        };

        // Log the request body
        console.log("Request body:", requestBody);
        console.log("API URL:", `https://projects-dev.nettlescs.com:5000/api/salesforce/project/${projectId}`);

        // Make the PUT request
        const response = await fetch(`https://projects-dev.nettlescs.com:5000/api/salesforce/project/${projectId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        // Log the response status
        console.log("Response status:", response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("API error response:", errorText);
          throw new Error(`API request failed with status ${response.status}: ${errorText}`);
        }

        // Get the response data
        const responseData = await response.json();
        console.log('Project updated successfully:', responseData);
        
        // Show success message
        showNotification("Project information updated successfully!");
        
        // Dispatch an event to notify other components
        const event = new CustomEvent('projectDetailsChanged', {
          detail: updatedData
        });
        window.dispatchEvent(event);
      } else {
        console.warn('No salesforceProjectId available for API update. Available fields:', Object.keys(initialData));
        showNotification("Project updated locally only. Could not update on server.", "warning");
      }

      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error saving project details:', error);
      // Show an error message to the user
      showNotification(`Failed to save project details: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // State values in the specified order
  const stateOptions = [
    "KS", "MO", "TX", "OK", "AR", "LA", "NE", "IA", 
    "AK", "AL", "AZ", "CA", "CO", "CT", "DC", "DE", 
    "FL", "GA", "HI", "ID", "IL", "IN", "KY", "MA", 
    "MD", "ME", "MI", "MN", "MS", "MT", "NC", "ND", 
    "NH", "NJ", "NM", "NV", "NY", "OH", "OR", "PA", 
    "PR", "RI", "SC", "SD", "TN", "UT", "VA", "VI", 
    "VT", "WA", "WI", "WV", "WY"
  ];
  
  // Tax exempt options
  const taxExemptOptions = [
    "Taxable",
    "Req'd Not Received",
    "Received",
    "Required"
  ];

  // Add these functions to the EditProjectModal component
  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // Add this function to parse the MM-DD-YYYY format back to ISO
  const parseDisplayDate = (displayDate) => {
    if (!displayDate) return '';
    const [month, day, year] = displayDate.split('-');
    return `${year}-${month}-${day}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {notification.show && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${
          notification.type === 'error' ? 'bg-red-500' : 
          notification.type === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
        } text-white`}>
          {notification.message}
        </div>
      )}
      
      <div className="bg-white w-full max-w-2xl p-6 rounded-lg shadow-xl">
        <div className="flex justify-between items-center mb-6 border-b pb-3">
          <h2 className="text-xl font-semibold text-gray-800">Edit Project Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">&times;</button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Customer</label>
            <input
              type="text"
              name="customerName"
              value={formData.customerName}
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 cursor-not-allowed"
              disabled
              readOnly
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Street</label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-red-500 focus:border-opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-red-500 focus:border-opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-red-500 focus:border-opacity-50"
              >
                <option value="">Select State</option>
                {stateOptions.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Zip</label>
              <div className="relative">
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  placeholder="5 digits"
                  className={`w-full border ${zipError ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:border-red-500 focus:border-opacity-50`}
                />
                {zipError && (
                  <div className="text-red-500 text-xs mt-1">{zipError}</div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Billing Date</label>
            <input
              type="text"
              name="billingDate"
              value={formatDateForDisplay(formData.billingDate)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 cursor-not-allowed"
              disabled
              readOnly
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Tax Exempt</label>
              <select
                name="taxExempt"
                value={formData.taxExempt}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-red-500 focus:border-opacity-50"
              >
                <option value="">Select Status</option>
                {taxExemptOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Certified Payroll</label>
              <input
                type="text"
                name="certifiedPayroll"
                value={formData.certifiedPayroll}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-red-500 focus:border-opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">P&P Bonds</label>
              <input
                type="text"
                name="ppBonds"
                value={formData.ppBonds}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-red-500 focus:border-opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">CCIP</label>
              <input
                type="text"
                name="ccip"
                value={formData.ccip}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-red-500 focus:border-opacity-50"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button 
            onClick={onClose} 
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            className="px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors flex items-center"
            disabled={isSubmitting || zipError}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditProjectModal;